import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {memo, useCallback} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {BodyText} from "@khanacademy/wonder-blocks-typography";

import {Section} from "../components/section/section.tsx";
import type {PlaybackRate} from "../types/account-settings-types.ts";

import {validatePlaybackRate} from "./validate-playback-rate.ts";

interface Props {
    muteVideos: boolean;
    showCaptions: boolean;
    playbackRate: PlaybackRate;
    disabled: boolean;
    onChangeMuteVideos: (muteVideos: boolean) => void;
    onChangeShowCaptions: (showCaptions: boolean) => void;
    onChangePlaybackRate: (playbackRate: PlaybackRate) => void;
    footer?: React.ReactNode;
}

export const StudentVideoSettings: React.FC<Props> = ({
    muteVideos,
    showCaptions,
    playbackRate,
    disabled,
    onChangeMuteVideos,
    onChangeShowCaptions,
    onChangePlaybackRate,
    footer,
}) => {
    return (
        <Section
            header={t`Video`}
            aria-label={t`Video settings`}
            children={
                <>
                    <View style={{gap: sizing.size_120}}>
                        <BodyText>{t`Customize how videos play during your lessons.`}</BodyText>
                    </View>
                    <View style={{marginBlockStart: sizing.size_240}} />
                    <Checkbox
                        label={t`Mute videos`}
                        id="mute-videos"
                        checked={muteVideos}
                        onChange={onChangeMuteVideos}
                        disabled={disabled}
                    />
                    <View style={{marginBlockStart: sizing.size_240}} />
                    <Checkbox
                        label={t`Show captions`}
                        id="show-captions"
                        checked={showCaptions}
                        onChange={onChangeShowCaptions}
                        disabled={disabled}
                    />
                    <View style={{marginBlockStart: sizing.size_240}} />
                    <PlaybackSpeed
                        onChangePlaybackRate={onChangePlaybackRate}
                        disabled={disabled}
                        playbackRate={playbackRate}
                    />
                </>
            }
            footer={footer}
        />
    );
};

// maps from the graphql enum value to the display string
const playbackRateItems = () => [
    {value: "RATE_25_PERCENT", label: t`.25`},
    {value: "RATE_50_PERCENT", label: t`.50`},
    {value: "RATE_75_PERCENT", label: t`.75`},
    {value: "RATE_100_PERCENT", label: t`Normal`},
    {value: "RATE_125_PERCENT", label: t`1.25`},
    {value: "RATE_150_PERCENT", label: t`1.5`},
    {value: "RATE_175_PERCENT", label: t`1.75`},
    {value: "RATE_200_PERCENT", label: t`2`},
];

const PlaybackSpeed = memo(
    ({
        onChangePlaybackRate,
        playbackRate = "RATE_100_PERCENT",
        disabled,
    }: Pick<Props, "onChangePlaybackRate" | "playbackRate" | "disabled">) => {
        const handlePlaybackRateChange = useCallback(
            (inputValue: string) => {
                const validatedPlaybackRate = validatePlaybackRate(inputValue);
                if (validatedPlaybackRate != null) {
                    onChangePlaybackRate(validatedPlaybackRate);
                }
            },
            [onChangePlaybackRate],
        );

        return (
            <LabeledField
                label={t`Playback speed`}
                field={
                    <SingleSelect
                        aria-label={t`Playback speed`}
                        onChange={handlePlaybackRateChange}
                        id="playback_rate_label"
                        placeholder={t`Playback speed`}
                        selectedValue={playbackRate}
                        disabled={disabled}
                        style={styles.playbackSpeedDropdown}
                    >
                        {playbackRateItems().map((rate) => (
                            <OptionItem
                                label={rate.label}
                                value={rate.value}
                                testId={rate.value}
                                key={rate.value}
                            />
                        ))}
                    </SingleSelect>
                }
            />
        );
    },
);

export default memo(StudentVideoSettings);

const styles = StyleSheet.create({
    playbackSpeedDropdown: {
        width: "100%",
    },
});
